import { EditOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client/react';
import { App, Button, Modal, Typography, theme } from 'antd';
import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { FragmentType } from '../../../graphql/generated';
import {
  AisImportType,
  BillingPreference,
  type DoctorInput,
  DoctorSalutation,
  type UpdateDoctorModal_DoctorFragment,
} from '../../../graphql/generated/graphql.ts';
import { SET_DOCTOR_AIS_LINKS_MUTATION } from './DoctorAisLinks.gql';
import { DoctorForm, type DoctorFormValues } from './DoctorForm';
import type { CatalogFragment, InsuranceFragment, LabFragment, SpecialRateFragment, TagFragment, ThemeFragment } from './DoctorForm.gql';
import { updateDoctorWithAisLinks } from './saveDoctorWithAisLinks';
import { UPDATE_DOCTOR_MUTATION } from './UpdateDoctorModal.gql';

const { useToken } = theme;

export const UpdateDoctorModal: React.FC<{
  allMeAddresses: string[];
  onClose: () => void;
  doctor: UpdateDoctorModal_DoctorFragment | null;
  labFragments: FragmentType<typeof LabFragment>[];
  catalogFragments: FragmentType<typeof CatalogFragment>[];
  insuranceFragments: FragmentType<typeof InsuranceFragment>[];
  specialRateFragments: FragmentType<typeof SpecialRateFragment>[];
  themeFragments: FragmentType<typeof ThemeFragment>[];
  tagFragments: FragmentType<typeof TagFragment>[];
}> = ({
  allMeAddresses,
  onClose,
  labFragments,
  doctor,
  catalogFragments,
  insuranceFragments,
  specialRateFragments,
  themeFragments,
  tagFragments,
}) => {
  const { token } = useToken();
  const [updateDoctorMutation, { loading }] = useMutation(UPDATE_DOCTOR_MUTATION);
  const [setAisLinks] = useMutation(SET_DOCTOR_AIS_LINKS_MUTATION);
  const [form] = useForm<DoctorFormValues>();
  const [hasError, setError] = useState(false);
  const { message } = App.useApp();

  const update = ({ aisLinks, ...input }: DoctorFormValues) =>
    updateDoctorWithAisLinks(doctor?.id ?? '', aisLinks, doctor?.aisLinks ?? [], {
      saveDoctor: () => updateDoctorMutation({ variables: { id: doctor?.id ?? '', input } }),
      saveLinks: (doctorId, links) => setAisLinks({ variables: { doctorId, links } }),
      message,
      close: closeAndReset,
      form,
    });

  const closeAndReset = () => {
    form.resetFields();
    setError(false);
    onClose();
  };

  useEffect(() => {
    form.setFieldsValue({
      salutation: doctor?.salutation ?? DoctorSalutation.HERR,
      name: doctor?.name ?? '',
      specialist: doctor?.specialist ?? '',
      hvNumber: doctor?.hvNumber ?? '',
      meAddress: doctor?.meAddress ?? '',
      firstName: doctor?.firstName ?? '',
      lastName: doctor?.lastName ?? '',
      preTitle: doctor?.preTitle ?? '',
      postTitle: doctor?.postTitle ?? '',
      street: doctor?.street ?? '',
      zip: doctor?.zip ?? '',
      city: doctor?.city ?? '',
      email: doctor?.email ?? '',
      phone: doctor?.phone ?? '',
      assignedLabs:
        doctor?.assignedLabs.map(al => ({
          labId: al.lab.id,
          customerId: al.customerId,
          supplier: al.supplier,
          runningNumber: al.runningNumber,
          overrideRunningNumber: false,
        })) ?? [],
      comment: doctor?.comment ?? '',
      aisImportType: doctor?.aisImportType ?? AisImportType.GENERIC_ANSI,
      aisImportDoctorId: doctor?.aisImportDoctorId ?? '',
      billingPreference: doctor?.billingPreference ?? BillingPreference.ASK,
      preferSpecialRate: doctor?.preferSpecialRate ?? false,
      autoDiagnose: doctor?.autoDiagnose ?? false,
      autoSpecialRate: doctor?.autoSpecialRate ?? false,
      catalogIds: doctor?.catalogs.map(c => c.id) ?? [],
      tagIds: doctor?.tags.map(t => t.id) ?? [],
      insuranceIds: doctor?.insurances.map(c => c.id) ?? [],
      specialRates:
        doctor?.specialRates.map(sr => ({
          maxUsagesPerPeriod: sr.maxUsagesPerPeriod,
          specialRateId: sr.specialRate.id,
        })) ?? [],
      phoneContacts:
        doctor?.phoneContacts.map(pc => ({
          name: pc.name,
          phoneNumber: pc.phoneNumber,
        })) ?? [],
      doctorBilling: doctor?.doctorBilling ?? false,
      patientBilling: doctor?.patientBilling ?? false,
      lgDoctorBilling: doctor?.lgDoctorBilling ?? false,
      lgInsuranceIds: doctor?.lgInsurances.map(c => c.id) ?? [],
      hasAis: doctor?.hasAis ?? false,
      printTransferDocument: doctor?.printTransferDocument ?? false,
      showPrices: doctor?.showPrices ?? true,
      showPriceSuggestion: doctor?.showPriceSuggestion ?? false,
      flipParamNames: doctor?.flipParamNames ?? false,
      addEmptyLabels: doctor?.addEmptyLabels ?? 0,
      aisExportTypes: doctor?.aisExportTypes ?? [],
      themeId: doctor?.theme?.id ?? null,
      csvImportEnabled: doctor?.csvImportEnabled ?? false,
      overrideRequestEnabled: doctor?.overrideRequestEnabled ?? false,
      disabled: doctor?.disabled ?? false,
      ecardEnabled: doctor?.ecardEnabled ?? false,
      aiEnabled: doctor?.aiEnabled ?? false,
      aiMonthlyTokenQuota: doctor?.aiMonthlyTokenQuota ?? null,
      aiPackage: doctor?.aiPackage ?? null,
      aiBillingLabId: doctor?.aiBillingLabId ?? null,
      // DatePicker works with Dayjs, while DoctorInput types the field as an ISO string
      installDate: (doctor?.installDate ? dayjs(doctor.installDate) : null) as unknown as DoctorInput['installDate'],
      zplLabelTemplate: doctor?.zplLabelTemplate ?? '',
      zplLabelWidth: doctor?.zplLabelWidth ?? 50,
      zplLabelHeight: doctor?.zplLabelHeight ?? 25,
      addZplLabels: doctor?.addZplLabels ?? 0,
      aisLinks: doctor?.aisLinks.map(({ vendor, externalId }) => ({ vendor, externalId })) ?? [],
    });
  }, [form, doctor]);

  return (
    <Modal
      width={1024}
      title="Zuweiser bearbeiten"
      open={!!doctor}
      destroyOnHidden
      onCancel={closeAndReset}
      forceRender
      footer={[
        <Typography.Text key="validationInfo" type="danger" style={{ marginRight: token.paddingSM }}>
          {hasError && 'Manche Felder sind nicht ordnungsgemäß ausgefüllt'}
        </Typography.Text>,
        <Button key="cancel" onClick={closeAndReset}>
          Abbrechen
        </Button>,
        <Button key="submit" icon={<EditOutlined />} type="primary" loading={loading} onClick={form.submit}>
          Speichern
        </Button>,
      ]}
    >
      <DoctorForm
        allMeAddresses={allMeAddresses.filter(it => it !== doctor?.meAddress)}
        form={form}
        onFinish={update}
        onFieldsChange={(_, allFields) => setError(allFields.some(it => it.errors?.length))}
        labFragments={labFragments}
        catalogFragments={catalogFragments}
        insuranceFragments={insuranceFragments}
        specialRateFragments={specialRateFragments}
        themeFragments={themeFragments}
        tagFragments={tagFragments}
      />
    </Modal>
  );
};
